import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import * as Sentry from "@sentry/react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import {
  GlobalChatProvider,
  GlobalChatManager,
} from "./components/chat";
import { isSchoolMode } from "./lib/school";
import Landing from "./pages/Landing";
import Pricing from "./pages/Pricing";
import Contact from "./pages/Contact";
import BetaTesting from "./pages/BetaTesting";
import BugReport from "./pages/BugReport";
import Privacy from "./pages/Privacy";
import DeleteAccount from "./pages/DeleteAccount";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import OnboardingGoals from "./pages/onboarding/Goals";
import OnboardingTime from "./pages/onboarding/Time";
import DiagnosticAssessmentPage from "./pages/assessment/Diagnostic";
import AssessmentResults from "./pages/assessment/Results";
import PersonalizedRoadmap from "./pages/PersonalizedRoadmap";
import PersonalizedModuleView from "./pages/PersonalizedModuleView";
import Arena from "./pages/Arena";
import SchoolArena from "./pages/SchoolArena";
import TechniqueDetail from "./pages/TechniqueDetail";
import PracticeArena from "./pages/PracticeArena";
import TournamentArena from "./pages/TournamentArena";
import ParentPortal from "./pages/ParentPortal";
import ParentComparison from "./pages/ParentComparison";
import StudentAnalytics from "./pages/StudentAnalytics";
import SamplePapers from "./pages/SamplePapers";
import SamplePaperTest from "./pages/SamplePaperTest";
import BlogList from "./pages/BlogList";
import BlogPost from "./pages/BlogPost";
import LessonPractice from "./pages/LessonPractice";
import LearningPaths from "./pages/LearningPaths";
import Competitive from "./pages/Competitive";
import TournamentResults from "./pages/TournamentResults";
import TournamentCreate from "./pages/TournamentCreate";
import Analytics from "./pages/Analytics";
import TeacherDashboard from "./pages/TeacherDashboard";
import TeacherComparison from "./pages/TeacherComparison";
import CreateAssignment from "./pages/CreateAssignment";
import AssignmentPractice from "./pages/AssignmentPractice";
import AssignmentSubmissions from "./pages/AssignmentSubmissions";
import Profile from "./pages/Profile";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import CheckoutCancel from "./pages/CheckoutCancel";
import Navigation from "./components/Navigation";
import FloatingFeedbackButton from "./components/FloatingFeedbackButton";
import { Toaster } from "./components/ui/toaster";
import SchoolAdminDashboard from "./pages/SchoolAdminDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import MultiplicationMasteryRoadmap from "./pages/MultiplicationMasteryRoadmap";
import MultiplicationSpecialCasesRoadmap from "./pages/MultiplicationSpecialCasesRoadmap";
import DivisionMasteryRoadmap from "./pages/DivisionMasteryRoadmap";
import IthopuCurriculum from "./pages/IthopuCurriculum";
import OlympiadPath from "./pages/OlympiadPath";
import LearningHub from "./pages/LearningHub";
import Certifications from "./pages/Certifications";
import CertificationExam from "./pages/CertificationExam";
import CertificateView from "./pages/CertificateView";
import VerifyCertificate from "./pages/VerifyCertificate";

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  // No need to check loading here as it's handled in AppRoutes
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Routes that parents are allowed to access
const PARENT_ALLOWED_ROUTES = [
  "/parent-portal",
  "/parent-portal/compare",
  "/student-analytics",
  "/profile",
  "/bug-report",
];

function ParentRestrictedRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If user is a parent, check if they're accessing an allowed route
  if (user.role === "parent") {
    const isAllowed = PARENT_ALLOWED_ROUTES.some((route) =>
      location.pathname.startsWith(route),
    );
    if (!isAllowed) {
      return <Navigate to="/parent-portal" replace />;
    }
  }

  return <>{children}</>;
}

// Guard that redirects school students away from restricted pages
function SchoolStudentGuard({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const isSchoolStudent = user?.role === "student" && !!user?.school_id;
  if (isSchoolStudent) return <Navigate to="/arena" replace />;
  return <>{children}</>;
}

// Renders SchoolArena for school students, regular Arena for everyone else
function ArenaSwitch() {
  const { user } = useAuth();
  const isSchoolStudent = user?.role === "student" && !!user?.school_id;
  return isSchoolStudent ? <SchoolArena /> : <Arena />;
}

// Helper to get the default route based on user role
function getDefaultRoute(user: { role: string } | null): string {
  if (!user) return "/login";
  if (user.role === "admin") return "/admin";
  if (user.role === "parent") return "/parent-portal";
  if (user.role === "teacher") return "/teacher";
  if (user.role === "school_admin") return "/school";
  return "/dashboard";
}

function AppRoutes() {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Show loading screen while checking authentication
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  // Determine if we're on the marketing site (ithopu.com) or app subdomain (app.ithopu.com)
  // Marketing site = ithopu.com, www.ithopu.com, localhost, or any vercel.app preview URL
  // App subdomain = only app.ithopu.com
  // School subdomains (e.g., modern.ithopu.com) are NOT marketing sites
  const hostname = window.location.hostname;
  const isAppSubdomain = hostname === "app.ithopu.com";
  const isMarketingSite = !isAppSubdomain && !isSchoolMode();

  // Debug logging
  console.log("🔍 Hostname Detection:");
  console.log("  hostname:", hostname);
  console.log("  isAppSubdomain:", isAppSubdomain);
  console.log("  isMarketingSite:", isMarketingSite);
  console.log("  Should show Landing page:", isMarketingSite);

  // Block school students from accessing app.ithopu.com
  // They should use their school subdomain instead
  if (isAppSubdomain && user && user.role === "student" && user.school_id) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center max-w-md p-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-3">School Account Detected</h2>
          <p className="text-gray-600 mb-4">
            Your account belongs to a school. Please log in through your school's portal instead.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Ask your teacher or school admin for the correct login URL.
          </p>
          <button
            className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
            onClick={async () => {
              const { supabase } = await import("./lib/supabase");
              await supabase.auth.signOut();
            }}
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  // Pages that should not show the Navigation bar
  const noNavPages = [
    "/",
    "/onboarding/goals",
    "/onboarding/time",
    "/assessment",
    "/assessment/results",
    "/checkout/success",
    "/checkout/cancel",
  ];
  const showNav = user && !noNavPages.includes(location.pathname);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {showNav && <Navigation />}
      <GlobalChatManager />
      <FloatingFeedbackButton />
      <Toaster />
      <Routes>
        <Route
          path="/login"
          element={!user ? <Login /> : <Navigate to={getDefaultRoute(user)} />}
        />
        <Route
          path="/register"
          element={
            !user ? <Register /> : <Navigate to={getDefaultRoute(user)} />
          }
        />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/beta-testing" element={<BetaTesting />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/delete-account" element={<DeleteAccount />} />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/checkout/success"
          element={
            <ProtectedRoute>
              <CheckoutSuccess />
            </ProtectedRoute>
          }
        />
        <Route path="/checkout/cancel" element={<CheckoutCancel />} />
        <Route
          path="/bug-report"
          element={
            <ProtectedRoute>
              <BugReport />
            </ProtectedRoute>
          }
        />
        <Route path="/blog" element={<BlogList />} />
        <Route path="/blog/:id" element={<BlogPost />} />
        <Route
          path="/dashboard"
          element={
            <ParentRestrictedRoute>
              <Dashboard />
            </ParentRestrictedRoute>
          }
        />
        <Route
          path="/arena"
          element={
            <ParentRestrictedRoute>
              <ArenaSwitch />
            </ParentRestrictedRoute>
          }
        />
        <Route
          path="/arena/multiplication-mastery"
          element={
            <ParentRestrictedRoute>
              <MultiplicationMasteryRoadmap />
            </ParentRestrictedRoute>
          }
        />
        <Route
          path="/arena/multiplication-special-cases"
          element={
            <ParentRestrictedRoute>
              <MultiplicationSpecialCasesRoadmap />
            </ParentRestrictedRoute>
          }
        />
        <Route
          path="/arena/divisibility-rules-mastery"
          element={
            <ParentRestrictedRoute>
              <DivisionMasteryRoadmap slug="divisibility-rules-mastery" />
            </ParentRestrictedRoute>
          }
        />
        <Route
          path="/arena/mental-division-techniques"
          element={
            <ParentRestrictedRoute>
              <DivisionMasteryRoadmap slug="mental-division-techniques" />
            </ParentRestrictedRoute>
          }
        />
        <Route
          path="/arena/ithopu-curriculum"
          element={
            <ParentRestrictedRoute>
              <IthopuCurriculum />
            </ParentRestrictedRoute>
          }
        />
        <Route
          path="/arena/essential-mental-math"
          element={
            <ParentRestrictedRoute>
              <DivisionMasteryRoadmap slug="essential-mental-math" />
            </ParentRestrictedRoute>
          }
        />
        <Route
          path="/arena/sat-math-speed-track"
          element={
            <ParentRestrictedRoute>
              <DivisionMasteryRoadmap slug="sat-math-speed-track" />
            </ParentRestrictedRoute>
          }
        />
        <Route
          path="/arena/olympiad-path"
          element={
            <ParentRestrictedRoute>
              <OlympiadPath />
            </ParentRestrictedRoute>
          }
        />
        <Route
          path="/arena/learning-hub"
          element={
            <ParentRestrictedRoute>
              <LearningHub />
            </ParentRestrictedRoute>
          }
        />
        <Route
          path="/learning-hub"
          element={
            <ParentRestrictedRoute>
              <LearningHub />
            </ParentRestrictedRoute>
          }
        />
        <Route
          path="/learning-paths"
          element={
            <ParentRestrictedRoute>
              <LearningPaths />
            </ParentRestrictedRoute>
          }
        />
        <Route
          path="/lesson/:lessonId"
          element={
            <ParentRestrictedRoute>
              <LessonPractice />
            </ParentRestrictedRoute>
          }
        />
        <Route
          path="/technique/:id"
          element={
            <ParentRestrictedRoute>
              <TechniqueDetail />
            </ParentRestrictedRoute>
          }
        />
        <Route
          path="/practice"
          element={
            <ParentRestrictedRoute>
              <PracticeArena />
            </ParentRestrictedRoute>
          }
        />
        <Route
          path="/tournament"
          element={
            <ParentRestrictedRoute>
              <TournamentArena />
            </ParentRestrictedRoute>
          }
        />
        <Route
          path="/competitive"
          element={
            <ParentRestrictedRoute>
              <SchoolStudentGuard>
                <Competitive />
              </SchoolStudentGuard>
            </ParentRestrictedRoute>
          }
        />
        <Route
          path="/tournament-results/:tournamentId"
          element={
            <ParentRestrictedRoute>
              <TournamentResults />
            </ParentRestrictedRoute>
          }
        />
        <Route
          path="/tournament-create"
          element={
            <ParentRestrictedRoute>
              <TournamentCreate />
            </ParentRestrictedRoute>
          }
        />
        <Route
          path="/analytics"
          element={
            <ParentRestrictedRoute>
              <Analytics />
            </ParentRestrictedRoute>
          }
        />
        <Route
          path="/parent-portal"
          element={
            <ProtectedRoute>
              <ParentPortal />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parent-portal/compare"
          element={
            <ProtectedRoute>
              <ParentComparison />
            </ProtectedRoute>
          }
        />
        <Route
          path="/student-analytics/:childId"
          element={
            <ProtectedRoute>
              <StudentAnalytics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/teacher"
          element={
            <ParentRestrictedRoute>
              <TeacherDashboard />
            </ParentRestrictedRoute>
          }
        />
        <Route
          path="/teacher/compare"
          element={
            <ParentRestrictedRoute>
              <TeacherComparison />
            </ParentRestrictedRoute>
          }
        />
        <Route
          path="/teacher/create-assignment"
          element={
            <ParentRestrictedRoute>
              <CreateAssignment />
            </ParentRestrictedRoute>
          }
        />
        <Route
          path="/teacher/edit-assignment/:assignmentId"
          element={
            <ParentRestrictedRoute>
              <CreateAssignment />
            </ParentRestrictedRoute>
          }
        />
        <Route
          path="/teacher/assignment-submissions"
          element={
            <ParentRestrictedRoute>
              <AssignmentSubmissions />
            </ParentRestrictedRoute>
          }
        />
        <Route
          path="/assignment/:assignmentId"
          element={
            <ParentRestrictedRoute>
              <AssignmentPractice />
            </ParentRestrictedRoute>
          }
        />
        <Route
          path="/sample-papers"
          element={
            <ParentRestrictedRoute>
              <SamplePapers />
            </ParentRestrictedRoute>
          }
        />
        <Route
          path="/sample-paper/:id"
          element={
            <ParentRestrictedRoute>
              <SamplePaperTest />
            </ParentRestrictedRoute>
          }
        />
        {/* Certification Routes */}
        <Route
          path="/certifications"
          element={
            <ParentRestrictedRoute>
              <Certifications />
            </ParentRestrictedRoute>
          }
        />
        <Route
          path="/certification-exam/:examId"
          element={
            <ParentRestrictedRoute>
              <CertificationExam />
            </ParentRestrictedRoute>
          }
        />
        <Route
          path="/certificate/:certId"
          element={
            <ProtectedRoute>
              <CertificateView />
            </ProtectedRoute>
          }
        />
        <Route
          path="/verify-certificate"
          element={<VerifyCertificate />}
        />
        {/* Onboarding & Assessment Routes */}
        <Route
          path="/onboarding/goals"
          element={
            <ProtectedRoute>
              <OnboardingGoals />
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding/time"
          element={
            <ProtectedRoute>
              <OnboardingTime />
            </ProtectedRoute>
          }
        />
        <Route
          path="/assessment"
          element={
            <ProtectedRoute>
              <DiagnosticAssessmentPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/assessment/results"
          element={
            <ProtectedRoute>
              <AssessmentResults />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-roadmap"
          element={
            <ParentRestrictedRoute>
              <SchoolStudentGuard>
                <PersonalizedRoadmap />
              </SchoolStudentGuard>
            </ParentRestrictedRoute>
          }
        />
        <Route
          path="/my-roadmap/module/:moduleId"
          element={
            <ParentRestrictedRoute>
              <SchoolStudentGuard>
                <PersonalizedModuleView />
              </SchoolStudentGuard>
            </ParentRestrictedRoute>
          }
        />
        {/* Platform Admin Routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/:tab"
          element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        {/* School Admin Routes */}
        <Route
          path="/school"
          element={
            <ProtectedRoute>
              <SchoolAdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/school/:tab"
          element={
            <ProtectedRoute>
              <SchoolAdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/"
          element={
            isMarketingSite ? (
              <Landing />
            ) : user ? (
              <Navigate to={getDefaultRoute(user)} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
      </Routes>
    </div>
  );
}

// Sentry Error Fallback Component
function ErrorFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="text-center p-8 max-w-md">
        <div className="text-6xl mb-4">⚠️</div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Something went wrong
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          We've been notified and are working to fix the issue.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Reload Page
        </button>
      </div>
    </div>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback} showDialog>
      <AuthProvider>
        <Router>
          <GlobalChatProvider>
            <AppRoutes />
          </GlobalChatProvider>
        </Router>
      </AuthProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
